<template>
  <aside class="admin-sidebar" :class="{ open: sidebarOpen }">
    <!-- Logo Section -->
    <div class="sidebar-logo">
      <router-link to="/admin" class="logo-link">
        <span class="logo-text">Feeds</span>
      </router-link>
    </div>

    <!-- Navigation -->
    <nav class="sidebar-nav">
      <!-- Main Section -->
      <div class="nav-section">
        <router-link to="/admin/mealplans" class="nav-item" :class="{ active: isActive('/admin/mealplans') }">
          <b-icon icon="calendar-week" size="is-small" />
          <span>Meal Plans</span>
        </router-link>
        <router-link to="/admin/recipes" class="nav-item" :class="{ active: isActive('/admin/recipes') }">
          <b-icon icon="food" size="is-small" />
          <span>Recipes</span>
        </router-link>
      </div>

      <div class="sidebar-divider"></div>

      <!-- Content Section -->
      <div class="nav-section">
        <div class="nav-section-title">Content</div>
        <router-link to="/admin/recipeCollections" class="nav-item" :class="{ active: isActive('/admin/recipeCollections') }">
          <b-icon icon="folder-multiple" size="is-small" />
          <span>Recipe Collections</span>
        </router-link>
        <router-link to="/admin/mealPlanCollections" class="nav-item" :class="{ active: isActive('/admin/mealPlanCollections') }">
          <b-icon icon="folder-multiple-outline" size="is-small" />
          <span>Meal Plan Collections</span>
        </router-link>
        <router-link to="/admin/tipsEncouragements" class="nav-item" :class="{ active: isActive('/admin/tipsEncouragements') }">
          <b-icon icon="lightbulb-outline" size="is-small" />
          <span>Tips</span>
        </router-link>
      </div>

      <div class="sidebar-divider"></div>

      <!-- Recipe Elements Section -->
      <div class="nav-section">
        <div class="nav-section-title">Recipe Elements</div>
        <router-link to="/admin/ingredients" class="nav-item" :class="{ active: isActive('/admin/ingredients') }">
          <b-icon icon="food-apple" size="is-small" />
          <span>Ingredients</span>
        </router-link>
        <router-link to="/admin/preps" class="nav-item" :class="{ active: isActive('/admin/preps') }">
          <b-icon icon="knife" size="is-small" />
          <span>Prep Terms</span>
        </router-link>
        <router-link to="/admin/units" class="nav-item" :class="{ active: isActive('/admin/units') }">
          <b-icon icon="scale" size="is-small" />
          <span>Units</span>
        </router-link>
        <router-link to="/admin/ingredientAisles" class="nav-item" :class="{ active: isActive('/admin/ingredientAisles') }">
          <b-icon icon="cart" size="is-small" />
          <span>Aisles</span>
        </router-link>
      </div>

      <div class="sidebar-divider"></div>

      <!-- Search & Tags Section -->
      <div class="nav-section">
        <div class="nav-section-title">Search & Tags</div>
        <router-link to="/admin/tags" class="nav-item" :class="{ active: isActive('/admin/tags') }">
          <b-icon icon="tag-multiple" size="is-small" />
          <span>Tag Management</span>
        </router-link>
        <router-link to="/admin/seasonalIngredients" class="nav-item" :class="{ active: isActive('/admin/seasonalIngredients') }">
          <b-icon icon="flower" size="is-small" />
          <span>Seasonal Ingredients</span>
        </router-link>
      </div>

      <div class="sidebar-divider"></div>

      <!-- Partner Section -->
      <div class="nav-section">
        <router-link to="/admin/partnerCampaigns" class="nav-item" :class="{ active: isActive('/admin/partnerCampaigns') }">
          <b-icon icon="handshake" size="is-small" />
          <span>Partner Campaigns</span>
        </router-link>
        <router-link to="/admin/users" class="nav-item" :class="{ active: isActive('/admin/users') }">
          <b-icon icon="account-group" size="is-small" />
          <span>Users</span>
        </router-link>
      </div>
    </nav>

    <!-- User Footer -->
    <div class="sidebar-footer" v-if="user">
      <div class="user-info">
        <b-icon icon="account-circle" size="is-small" />
        <span class="user-name">{{ user.displayName }}</span>
      </div>
      <button class="logout-btn" @click="logout" title="Logout">
        <b-icon icon="logout" size="is-small" />
      </button>
    </div>
  </aside>
</template>

<script>
import { mapState } from 'vuex'
import auth from '@/firebase/auth'

export default {
  name: 'AdminSidebar',
  computed: {
    ...mapState(['user', 'isAuthenticated', 'sidebarOpen'])
  },
  watch: {
    '$route'() {
      this.$store.commit('setSidebarOpen', false)
    }
  },
  methods: {
    isActive(path) {
      return this.$route.path.startsWith(path)
    },
    logout() {
      auth.signOut()
        .then(() => {
          this.$store.commit('setUser', null)
          this.$router.push({ name: 'LoginView' })
        })
    }
  }
}
</script>

<style scoped>
.admin-sidebar {
  width: var(--sidebar-width);
  background: var(--sidebar-gradient);
  color: white;
  display: flex;
  flex-direction: column;
  position: fixed;
  top: 0;
  left: 0;
  bottom: 0;
  z-index: var(--z-sidebar);
  overflow-y: auto;
}

.sidebar-logo {
  padding: 24px;
  border-bottom: 1px solid rgba(255,255,255,0.08);
}

.logo-link {
  text-decoration: none;
}

.logo-text {
  font-family: 'Nunito', sans-serif;
  font-weight: 800;
  font-size: 28px;
  letter-spacing: -0.5px;
  background: linear-gradient(135deg, #fff 0%, rgba(255,255,255,0.85) 100%);
  -webkit-background-clip: text;
  -webkit-text-fill-color: transparent;
  background-clip: text;
}

.sidebar-nav {
  flex: 1;
  padding: 20px 0;
  overflow-y: auto;
}

.nav-section {
  margin-bottom: 8px;
}

.nav-section-title {
  font-size: 10px;
  text-transform: uppercase;
  letter-spacing: 1.2px;
  color: rgba(255,255,255,0.4);
  padding: 16px 24px 8px;
  font-weight: 600;
}

.nav-item {
  display: flex;
  align-items: center;
  gap: 12px;
  padding: 11px 24px;
  color: rgba(255,255,255,0.7);
  text-decoration: none;
  font-size: 14px;
  font-weight: 500;
  transition: all 0.2s ease;
  cursor: pointer;
  border-left: 3px solid transparent;
  margin: 2px 0;
}

.nav-item:hover {
  background: rgba(255,255,255,0.06);
  color: white;
}

.nav-item.active {
  background: rgba(255,255,255,0.1);
  color: white;
  border-left-color: var(--feeds-orange);
}

.nav-item .icon {
  opacity: 0.7;
}

.nav-item.active .icon {
  opacity: 1;
}

.sidebar-divider {
  height: 1px;
  background: rgba(255,255,255,0.08);
  margin: 12px 24px;
}

.sidebar-footer {
  padding: 16px 24px;
  border-top: 1px solid rgba(255,255,255,0.08);
  display: flex;
  align-items: center;
  justify-content: space-between;
}

.user-info {
  display: flex;
  align-items: center;
  gap: 8px;
  color: rgba(255,255,255,0.7);
  font-size: 13px;
}

.user-name {
  max-width: 120px;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.logout-btn {
  background: none;
  border: none;
  color: rgba(255,255,255,0.5);
  cursor: pointer;
  padding: 4px 8px;
  transition: all 0.2s ease;
  border-radius: var(--radius-sm);
  display: flex;
  align-items: center;
  justify-content: center;
}

.logout-btn:hover {
  background: rgba(255,255,255,0.1);
  color: var(--feeds-orange);
}

@media (max-width: 768px) {
  .admin-sidebar {
    transform: translateX(-100%);
    transition: transform 0.3s ease;
    width: 280px;
    z-index: 200;
  }

  .admin-sidebar.open {
    transform: translateX(0);
  }
}
</style>
